/**
 * Keycloak Configuration Check Service
 *
 * Monitors changes in Keycloak configuration and clears localStorage/cookies
 * when APP_KEYCLOAK_CLIENT_ID, APP_KEYCLOAK_REALM, or APP_KEYCLOAK_URL changes
 * Takes into account user type (SYSTEM vs NORMAL) to validate correct configuration
 *
 * @ngdoc service
 * @name mwApp/common.service:keycloakConfigCheckSvc
 */
/* @ngInject */
export default function KeycloakConfigCheckSvc ($window, $log, storageSvc) {
  const KEYCLOAK_CONFIG_HASH_KEY = 'KEYCLOAK_CONFIG_HASH'

  /**
   * Determine if user is a Setplex (SYSTEM) user
   * @private
   * @returns {boolean} True if user is SYSTEM type
   */
  function isSetplexUser () {
    const userType = storageSvc.local.get('userType')
    return userType === 'SYSTEM'
  }

  /**
   * Determine current page type (login or setplex-login)
   * @private
   * @returns {boolean} True if this is Setplex login page
   */
  function isSetplexLoginPage () {
    if (typeof $window === 'undefined' || !$window.location) {
      return false
    }
    const path = $window.location.pathname
    const hash = $window.location.hash
    const isSetplex = path.includes('/setplex-login') ||
                      path.includes('/setplex-user-403') ||
                      hash.includes('/setplex-login') ||
                      hash.includes('/setplex-user-403')

    $log.info('[keycloak-config-check] Checking page - path:', path, 'hash:', hash, 'isSetplex:', isSetplex)
    return isSetplex
  }

  /**
   * Get the appropriate Keycloak configuration from ENV based on user type
   * @private
   * @param {Object} env - Environment variables object
   * @returns {Object} Configuration object with clientId, realm, url
   */
  function getKeycloakConfigByUserType (env) {
    const userType = storageSvc.local.get('userType')

    // Determine if this is Setplex login by:
    // 1. userType (if already authenticated)
    // 2. current page path (if on login page)
    const isSetplex = isSetplexUser() || isSetplexLoginPage()

    $log.info('[keycloak-config-check] Determining config - userType:', userType, 'isSetplex:', isSetplex)

    // Always get configuration from ENV (not from localStorage!)
    // This ensures we check what SHOULD be, not what WAS
    return {
      clientId: isSetplex
        ? (env.APP_KEYCLOAK_CLIENT_ID_SETPLEX || env.APP_KEYCLOAK_CLIENT_ID || '')
        : (env.APP_KEYCLOAK_CLIENT_ID || ''),
      realm: isSetplex
        ? (env.APP_KEYCLOAK_REALM_SETPLEX || env.APP_KEYCLOAK_REALM_SETPLEX || '')
        : (env.APP_KEYCLOAK_REALM || ''),
      url: isSetplex
        ? (env.APP_KEYCLOAK_URL_SETPLEX || env.APP_KEYCLOAK_URL || '')
        : (env.APP_KEYCLOAK_URL || ''),
      userType: userType || (isSetplex ? 'SYSTEM' : 'NORMAL')
    }
  }

  /**
   * Simple hash function to create a hash from Keycloak configuration
   * @private
   * @param {Object} config - Keycloak configuration object
   * @returns {string} Hash string
   */
  function hashKeycloakConfig (config) {
    const keycloakConfig = getKeycloakConfigByUserType(config)
    const str = JSON.stringify(keycloakConfig)

    // Simple hash function (djb2)
    let hash = 5381
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) + str.charCodeAt(i) // hash * 33 + c
    }
    return hash.toString(36)
  }

  /**
   * Get stored configuration hash from localStorage
   * @private
   * @returns {string|null} Stored hash or null if not found
   */
  function getStoredConfigHash () {
    try {
      return storageSvc.local.get(KEYCLOAK_CONFIG_HASH_KEY)
    } catch (e) {
      $log.error('[keycloak-config-check] Error reading stored hash:', e)
      return null
    }
  }

  /**
   * Store configuration hash to localStorage
   * @private
   * @param {string} hash - Hash string to store
   */
  function storeConfigHash (hash) {
    try {
      storageSvc.local.set(KEYCLOAK_CONFIG_HASH_KEY, hash)
    } catch (e) {
      $log.error('[keycloak-config-check] Error storing hash:', e)
    }
  }

  /**
   * Clear only Keycloak-related data from localStorage
   * @private
   */
  function clearKeycloakData () {
    try {
      $log.info('[keycloak-config-check] Clearing Keycloak-related data from localStorage...')
      storageSvc.local.remove('KEYCLOAK_URL')
      storageSvc.local.remove('KEYCLOAK_REALM')
      storageSvc.local.remove('KEYCLOAK_CLIENT_ID')
      storageSvc.local.remove('KEYCLOAK_TOKEN')
      storageSvc.local.remove('userType')
      storageSvc.local.remove('authenticated')
      storageSvc.local.remove('user')
      $log.info('[keycloak-config-check] Keycloak data cleared')
    } catch (e) {
      $log.error('[keycloak-config-check] Error clearing Keycloak data:', e)
    }
  }

  /**
   * Clear all cookies
   * @private
   */
  function clearAllCookies () {
    try {
      $log.info('[keycloak-config-check] Clearing cookies...')
      storageSvc.cookies.clear()
      $log.info('[keycloak-config-check] Cookies cleared')
    } catch (e) {
      $log.error('[keycloak-config-check] Error clearing cookies:', e)
    }
  }

  /**
   * Check if Keycloak configuration has changed and clear storage if needed
   * @param {Object} env - Environment variables object (window.ENV)
   * @param {Object} authSvc - Auth service instance for logout
   * @returns {boolean} True if configuration changed and storage was cleared
   */
  this.checkAndClear = function (env, authSvc) {
    // Skip check if Keycloak is not enabled
    if (!env.APP_KEYCLOAK_ENABLED) {
      $log.info('[keycloak-config-check] Keycloak is disabled, skipping check')
      return false
    }

    const currentConfig = getKeycloakConfigByUserType(env)
    $log.info('[keycloak-config-check] User type:', currentConfig.userType)
    $log.info('[keycloak-config-check] Expected config from ENV:', {
      clientId: currentConfig.clientId,
      realm: currentConfig.realm,
      url: currentConfig.url
    })

    // ALWAYS sync localStorage with ENV values
    // This ensures initKC() always gets correct values
    const storedUrl = storageSvc.local.get('KEYCLOAK_URL')
    const storedRealm = storageSvc.local.get('KEYCLOAK_REALM')
    const storedClientId = storageSvc.local.get('KEYCLOAK_CLIENT_ID')

    if (storedUrl || storedRealm || storedClientId) {
      $log.info('[keycloak-config-check] Currently stored config:', {
        clientId: storedClientId,
        realm: storedRealm,
        url: storedUrl
      })
    }

    // Check if stored values differ from ENV
    const needsUpdate = storedUrl !== currentConfig.url ||
                        storedRealm !== currentConfig.realm ||
                        storedClientId !== currentConfig.clientId

    if (needsUpdate && (storedUrl || storedRealm || storedClientId)) {
      $log.warn('[keycloak-config-check] localStorage has OLD values, syncing with ENV...')
      try {
        storageSvc.local.set('KEYCLOAK_URL', currentConfig.url)
        storageSvc.local.set('KEYCLOAK_REALM', currentConfig.realm)
        storageSvc.local.set('KEYCLOAK_CLIENT_ID', currentConfig.clientId)
        storageSvc.local.remove('KEYCLOAK_TOKEN')
        $log.info('[keycloak-config-check] Synced to:', {
          url: currentConfig.url,
          realm: currentConfig.realm,
          clientId: currentConfig.clientId
        })
      } catch (e) {
        $log.error('[keycloak-config-check] Error syncing values:', e)
      }
    }

    // Now check hash
    const storedHash = getStoredConfigHash()
    if (!storedHash) {
      // Check if we have KEYCLOAK_* in localStorage (means we just logged in)
      const hasKeycloakConfig = storageSvc.local.get('KEYCLOAK_URL') &&
                                storageSvc.local.get('KEYCLOAK_REALM') &&
                                storageSvc.local.get('KEYCLOAK_CLIENT_ID')

      if (hasKeycloakConfig) {
        // Second check after login - store hash
        const currentHash = hashKeycloakConfig(env)
        storeConfigHash(currentHash)
        $log.info('[keycloak-config-check] First login completed, storing configuration hash:', currentHash)
        return false
      }

      // First check before login - skip hash comparison but localStorage already synced
      $log.info('[keycloak-config-check] First visit, localStorage synced with ENV')
      return false
    }

    // Calculate current configuration hash based on ENV
    const currentHash = hashKeycloakConfig(env)

    $log.info('[keycloak-config-check] Current hash (from ENV):', currentHash)
    $log.info('[keycloak-config-check] Stored hash:', storedHash)

    // Configuration has changed
    if (currentHash !== storedHash) {
      $log.warn('[keycloak-config-check] Keycloak configuration changed!')
      $log.warn('[keycloak-config-check] This may indicate:')
      $log.warn('[keycloak-config-check] 1. Environment variables changed')
      $log.warn('[keycloak-config-check] 2. User type changed but config mismatch detected')
      $log.warn('[keycloak-config-check] Logging out and clearing storage...')

      // CRITICAL: Update KEYCLOAK_* in localStorage SYNCHRONOUSLY with new values from ENV
      // This forces initKC() to use correct values even if it reads from localStorage
      $log.info('[keycloak-config-check] Updating Keycloak values in localStorage with new ENV values (sync)...')
      try {
        storageSvc.local.set('KEYCLOAK_URL', currentConfig.url)
        storageSvc.local.set('KEYCLOAK_REALM', currentConfig.realm)
        storageSvc.local.set('KEYCLOAK_CLIENT_ID', currentConfig.clientId)
        storageSvc.local.remove('KEYCLOAK_TOKEN') // Clear token as it's invalid now
        $log.info('[keycloak-config-check] Updated to:', {
          url: currentConfig.url,
          realm: currentConfig.realm,
          clientId: currentConfig.clientId
        })
      } catch (e) {
        $log.error('[keycloak-config-check] Error updating Keycloak values:', e)
      }

      // Clear Keycloak data and cookies ONLY when config changed
      clearKeycloakData()
      clearAllCookies()

      // Store new hash
      storeConfigHash(currentHash)
      $log.info('[keycloak-config-check] New configuration hash stored')

      // Check if user is authenticated
      const isAuthenticated = storageSvc.local.get('authenticated')

      if (isAuthenticated && authSvc && typeof authSvc.doLogout === 'function') {
        // Call logout to notify Keycloak server
        $log.info('[keycloak-config-check] Performing logout...')
        try {
          authSvc.doLogout()
            .catch(err => {
              $log.error('[keycloak-config-check] Logout error:', err)
            })
            .finally(() => {
              $log.info('[keycloak-config-check] Reloading page...')
              setTimeout(() => {
                $window.location.reload()
              }, 100)
            })
        } catch (err) {
          $log.error('[keycloak-config-check] Logout failed:', err)
          $log.info('[keycloak-config-check] Reloading page anyway...')
          setTimeout(() => {
            $window.location.reload()
          }, 100)
        }
      } else {
        // Not authenticated, just reload
        $log.info('[keycloak-config-check] User not authenticated, reloading page...')
        setTimeout(() => {
          $window.location.reload()
        }, 100)
      }

      return true
    }

    $log.info('[keycloak-config-check] Configuration unchanged')
    return false
  }

  /**
   * Get current Keycloak configuration for debugging
   * @param {Object} env - Environment variables object (window.ENV)
   * @returns {Object} Current Keycloak configuration based on user type
   */
  this.getConfig = function (env) {
    const config = getKeycloakConfigByUserType(env)
    return {
      ...config,
      enabled: env.APP_KEYCLOAK_ENABLED || false,
      isSetplexUser: isSetplexUser()
    }
  }

  /**
   * Manually reset Keycloak configuration hash (for testing/debugging)
   */
  this.reset = function () {
    $log.info('[keycloak-config-check] Manually resetting configuration hash')
    try {
      storageSvc.local.remove(KEYCLOAK_CONFIG_HASH_KEY)
      $log.info('[keycloak-config-check] Configuration hash removed')
    } catch (e) {
      $log.error('[keycloak-config-check] Error removing hash:', e)
    }
  }

  /**
   * Get current hash
   * @param {Object} env - Environment variables object
   * @returns {string} Current configuration hash
   */
  this.getCurrentHash = function (env) {
    return hashKeycloakConfig(env)
  }

  /**
   * Get stored hash
   * @returns {string|null} Stored configuration hash
   */
  this.getStoredHash = function () {
    return getStoredConfigHash()
  }

  // Expose to window for debugging in console
  // Note: For debug purposes, authSvc might not be available, so logout won't be performed
  if (typeof $window !== 'undefined') {
    $window.__keycloakConfigDebug = {
      check: (authSvcInstance) => this.checkAndClear($window.ENV, authSvcInstance),
      getConfig: () => this.getConfig($window.ENV),
      reset: () => this.reset(),
      getCurrentHash: () => this.getCurrentHash($window.ENV),
      getStoredHash: () => this.getStoredHash()
    }
  }
}
